import { subscribeToApiResults } from "@jugl-web/rest-api";
import { useToast } from "@jugl-web/utils";
import { FC, PropsWithChildren, useEffect, useMemo } from "react";
import { useApiResultHandler } from "../../hooks/useApiResultHandler";
import {
  ApiResultHandlingConfig,
  ApiResultHandlingContext,
} from "./ApiResultHandlingContext";

// Separate child component so the pipeline reads the config provided by the
// surrounding ApiResultHandlingContext.Provider.
const GlobalApiResultSubscriber: FC = () => {
  const { handleApiError } = useApiResultHandler();
  const { toast } = useToast();

  useEffect(
    () =>
      subscribeToApiResults((event) => {
        if (event.type === "success") {
          toast(event.successMessage, { variant: "success" });
          return;
        }
        handleApiError(event.rawError, event.meta);
      }),
    [handleApiError, toast]
  );

  return null;
};

/**
 * Owns global handling of API request results (default error toasts and
 * optional success toasts) and injects app-specific behavior (special-error
 * routing, error reporting). Requires apiResultMiddleware in the app's store
 * and must be mounted below SnackbarProvider and IntlProvider.
 */
export const ApiResultHandlingProvider: FC<
  PropsWithChildren<ApiResultHandlingConfig>
> = ({ children, onSpecialError, onErrorReported }) => {
  const contextValue = useMemo(
    () => ({ onSpecialError, onErrorReported }),
    [onSpecialError, onErrorReported]
  );

  return (
    <ApiResultHandlingContext.Provider value={contextValue}>
      <GlobalApiResultSubscriber />
      {children}
    </ApiResultHandlingContext.Provider>
  );
};
